import { Component, effect, inject, input, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DesignSystemModule } from '@compeople-shared/design-system-angular';
import { provideTranslocoScope, TranslocoPipe } from '@jsverse/transloco';
import { DragAndDropComponent } from '../../../components/drag-and-drop/drag-and-drop.component';
import { ManufacturerOrderDTO, ManufacturerService } from '@api';
import { DatePipe } from '@angular/common';
import { ModalService } from '@services/modal.service';
import { noWhitespaceValidator } from '@validators/no-whitespace.validator';
import { ImageUploadResult, ModalDataArtist } from '@utils/interfaces';
import { ModalMode, RefreshChannel } from '@utils/enums';
import { lastValueFrom } from 'rxjs';
import { ModalModePipe } from '@pipes/modal-mode.pipe';
import { ImageSwitchAreaComponent } from '../../../components/image-switch-area/image-switch-area.component';
import { BorderedImageComponent } from '../../../components/bordered-image/bordered-image.component';
import { ManufacturerOrderFormValue } from '@utils/types';
import { RefreshStore } from '@stores/refresh.store';

@Component({
  selector: 'art-artist-modal',
  imports: [
    DesignSystemModule,
    ReactiveFormsModule,
    TranslocoPipe,
    DragAndDropComponent,
    DatePipe,
    ModalModePipe,
    ImageSwitchAreaComponent,
    BorderedImageComponent,
  ],
  templateUrl: './artist-modal.component.html',
  styleUrls: ['./artist-modal.component.scss'],
  providers: [provideTranslocoScope('artist')],
})
export class ArtistModalComponent implements OnInit {
  protected readonly ModalMode = ModalMode;
  readonly manufacturerService = inject(ManufacturerService);
  readonly modalService = inject(ModalService);
  readonly refreshStore = inject(RefreshStore);

  artistForm = new FormGroup({
    firstName: new FormControl<string | null>(null, [Validators.required, noWhitespaceValidator()]),
    lastName: new FormControl<string | null>(null, [Validators.required, noWhitespaceValidator()]),
    gender: new FormControl<ManufacturerOrderDTO.GenderEnum | null>(null, Validators.required),
    birthDate: new FormControl<string | null>(null),
    deathDate: new FormControl<string | null>(null),
    status: new FormControl<ManufacturerOrderDTO.StatusEnum | null>(null, Validators.required),
    biography: new FormControl<string | null>(null),
    uploadedImageId: new FormControl<string | null>(null),
    imageUri: new FormControl<string | null>(null),
  });
  originalArtistFormValue: ManufacturerOrderFormValue | null = null;
  genderOptions = [
    { value: ManufacturerOrderDTO.GenderEnum.FEMALE, label: 'female' },
    { value: ManufacturerOrderDTO.GenderEnum.MALE, label: 'male' },
    { value: ManufacturerOrderDTO.GenderEnum.DIVERSE, label: 'diverse' },
  ];
  statusOptions = [
    { value: ManufacturerOrderDTO.StatusEnum.ACTIVE, label: 'active' },
    { value: ManufacturerOrderDTO.StatusEnum.INACTIVE, label: 'inactive' },
  ];
  today = new Date();

  manufacturerFullName = signal<string | null>(null);
  imageUrl = signal<string | null>(null);
  showDragAndDrop = signal(false);
  isLoading = signal(false);
  statusError = signal(false);
  dateErrorMessage = signal('');
  data = input<ModalDataArtist | undefined>();

  mode = signal<ModalMode | undefined>(undefined);

  get isFormBlocked() {
    return this.artistForm.invalid || this.dateErrorMessage() !== '' || this.isLoading() || this.statusError();
  }

  constructor() {
    effect(() => {
      if (this.showDragAndDrop()) {
        this.artistForm.markAsDirty();
      }
    });

    effect(() => {
      const data = this.data();
      if (data) {
        this.mode.set(data.mode);
      }
    });
  }

  async ngOnInit() {
    if (this.data()?.mode === ModalMode.VIEW) {
      await this.getArtistAndInitializeForm();
    }
  }

  async getArtistAndInitializeForm() {
    const id = this.data()?.payload;
    if (id === null || id === undefined) return;

    const manufacturer = await lastValueFrom(this.manufacturerService.getManufacturersById(id));
    if (manufacturer) {
      this.manufacturerFullName.set(`${manufacturer.firstName ?? ''} ${manufacturer.lastName}`);
      this.artistForm.patchValue({
        firstName: manufacturer.firstName,
        lastName: manufacturer.lastName,
        gender: manufacturer.gender,
        birthDate: manufacturer.dateOfBirth,
        deathDate: manufacturer.dateOfDeath,
        status: manufacturer.status,
        uploadedImageId: manufacturer.imageId,
        imageUri: manufacturer.signedImageUri,
        biography: manufacturer.biography,
      });
      this.imageUrl.set(manufacturer.signedImageUri ?? null);
    }
  }

  close() {
    this.modalService.close();
  }

  async uploadImage(event: ImageUploadResult) {
    this.artistForm.patchValue({ uploadedImageId: event.imageId, imageUri: event.imageUrl });
    this.showDragAndDrop.set(false);
    this.imageUrl.set(event.imageUrl);
    this.artistForm.markAsDirty();
  }

  async createArtist() {
    if (this.isFormBlocked) return;
    const manufacturer = this.createManufacturerOrder();

    this.isLoading.set(true);
    try {
      await lastValueFrom(this.manufacturerService.createManufacturers(manufacturer));
      this.close();
    } finally {
      this.isLoading.set(false);
    }
  }

  editArtist() {
    this.originalArtistFormValue = { ...this.artistForm.value };
    this.mode.set(ModalMode.EDIT);
  }

  viewArtist() {
    this.artistForm.markAsPristine();
    this.mode.set(ModalMode.VIEW);
  }

  cancelEdit() {
    this.artistForm.patchValue({ ...this.originalArtistFormValue });
    this.imageUrl.set(this.artistForm.value.imageUri ?? null);
    this.viewArtist();
    this.showDragAndDrop.set(false);
  }

  async saveEditedArtist() {
    const id = this.data()?.payload;
    if (this.isFormBlocked || id == null) return;

    if (this.showDragAndDrop()) {
      this.artistForm.patchValue({ uploadedImageId: null, imageUri: null });
      this.imageUrl.set(null);
    }

    this.isLoading.set(true);

    try {
      const manufacturer = this.createManufacturerOrder();
      await lastValueFrom(this.manufacturerService.updateManufacturers(id, manufacturer));
      this.manufacturerFullName.set(`${manufacturer.firstName ?? ''} ${manufacturer.lastName}`);
      this.viewArtist();
      this.refreshStore.trigger(RefreshChannel.Inventory);
    } finally {
      this.isLoading.set(false);
    }
  }

  createManufacturerOrder(): ManufacturerOrderDTO {
    const formValue = this.artistForm.value;
    return {
      firstName: formValue.firstName!,
      lastName: formValue.lastName!,
      dateOfBirth: formValue.birthDate ?? undefined,
      dateOfDeath: formValue.deathDate ?? undefined,
      gender: formValue.gender!,
      status: formValue.status!,
      imageId: formValue.uploadedImageId ?? undefined,
      biography: formValue.biography ?? undefined,
    };
  }

  checkDateError() {
    if (this.artistForm?.value?.birthDate && this.artistForm?.value?.deathDate) {
      this.dateErrorMessage.set(
        this.artistForm?.value?.birthDate > this.artistForm?.value?.deathDate
          ? 'error-message.birthdate-date-after-death'
          : '',
      );
    } else {
      this.dateErrorMessage.set('');
    }
  }

  changeDeathDate(event: CustomEvent) {
    this.checkDateError();
    if (event.detail != null) {
      this.artistForm.patchValue({ status: ManufacturerOrderDTO.StatusEnum.INACTIVE });
    }
    this.checkStatusError();
  }

  checkStatusError() {
    if (
      this.artistForm?.value?.deathDate != null &&
      this.artistForm?.value?.status === ManufacturerOrderDTO.StatusEnum.ACTIVE
    ) {
      this.statusError.set(true);
    } else {
      this.statusError.set(false);
    }
  }
}
